import { useCallback, useEffect, useState } from "react";
import { randomString } from "utils/randomString";

// ── Wspólny mechanizm „filtrów własnych" list typu grid ─────────────────────
//
// Jeden zapisany filtr = etykieta + migawka kryteriów panelu „Filtr" danej listy.
// Trwałość: localStorage (żywy, synchroniczny cache) mirrorowany do BE przez
// utils/appSettingsSync (klucz musi być zarejestrowany ze scope "user"). Dzięki
// temu filtry przenoszą się między urządzeniami/sesjami użytkownika.
//
// Reaguje na zdarzenie `app-settings-hydrated` (emitowane po hydratacji BE→LS
// przy logowaniu / zmianie profilu), by po dociągnięciu danych z BE odświeżyć
// listę bez przeładowania widoku.

export interface SavedFilterEntry<T> {
  Id: string;
  Name: string;
  Criteria: T;
}

export interface UseSavedFilters<T> {
  saved: SavedFilterEntry<T>[];
  save: (name: string, criteria: T) => void;
  remove: (id: string) => void;
}

export function useSavedFilters<T>(storageKey: string): UseSavedFilters<T> {
  const [saved, setSaved] = useState<SavedFilterEntry<T>[]>([]);

  const load = useCallback(() => {
    try {
      setSaved(JSON.parse(localStorage.getItem(storageKey) || "[]"));
    } catch {
      setSaved([]);
    }
  }, [storageKey]);

  useEffect(() => {
    load();
    const onHydrated = (e: Event) => {
      const keys = (e as CustomEvent).detail?.keys as string[] | undefined;
      // Brak listy kluczy = odśwież zawsze; z listą = tylko gdy dotyczy nas.
      if (!keys || keys.includes(storageKey)) load();
    };
    window.addEventListener("app-settings-hydrated", onHydrated);
    return () =>
      window.removeEventListener("app-settings-hydrated", onHydrated);
  }, [load, storageKey]);

  // Zapis funkcyjny (prev => next) — bez nieaktualnych domknięć nad `saved`.
  const persist = useCallback(
    (updater: (prev: SavedFilterEntry<T>[]) => SavedFilterEntry<T>[]) => {
      setSaved((prev) => {
        const next = updater(prev);
        localStorage.setItem(storageKey, JSON.stringify(next));
        return next;
      });
    },
    [storageKey],
  );

  const save = useCallback(
    (name: string, criteria: T) => {
      persist((prev) => [
        ...prev,
        {
          Id: randomString(),
          Name: name.trim() || "Nowy filtr",
          Criteria: criteria,
        },
      ]);
    },
    [persist],
  );

  const remove = useCallback(
    (id: string) => persist((prev) => prev.filter((x) => x.Id !== id)),
    [persist],
  );

  return { saved, save, remove };
}
